import React from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Label } from '@/components/ui/label'
import {
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  UserCheck,
  AlertTriangle,
} from 'lucide-react'
import { VoucherResponse } from '@/app/types/voucher'
import { PartySelector } from '@/app/components/common/PartySelector'

interface BulkAgentAssignmentModalProps {
  isOpen: boolean
  onClose: () => void
  invoices: VoucherResponse[]
  selectedAgentId: number | null
  onAgentChange: (agentId: number | null) => void
  onConfirm: () => void
  isProcessing: boolean
  progress?: {
    current: number
    total: number
    currentInvoice?: string
    skipped?: number
    errors?: string[]
  }
}

export const BulkAgentAssignmentModal: React.FC<BulkAgentAssignmentModalProps> = ({
  isOpen,
  onClose,
  invoices,
  selectedAgentId,
  onAgentChange,
  onConfirm,
  isProcessing,
  progress,
}) => {
  const progressPercentage = progress ? (progress.current / progress.total) * 100 : 0

  // Filter out cancelled invoices
  const validInvoices = invoices.filter(inv => !inv.is_cancelled)
  const cancelledCount = invoices.length - validInvoices.length

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5" />
            Bulk Assign Field Agent
          </DialogTitle>
          <DialogDescription>
            Assign a field agent to delivery challans for {validInvoices.length} invoice{validInvoices.length !== 1 ? 's' : ''}.
            {cancelledCount > 0 && (
              <span className="text-warning ml-1">
                ({cancelledCount} cancelled invoice{cancelledCount !== 1 ? 's' : ''} will be skipped)
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {!isProcessing && (
          <>
            <div className="space-y-4">
              {/* Agent Selection */}
              <div>
                <Label htmlFor="agent">Field Agent</Label>
                <PartySelector
                  value={selectedAgentId ?? undefined}
                  onChange={(value) => onAgentChange(value ?? null)}
                  placeholder="Select field agent..."
                  filterPartyGroup="Employee"
                  disabled={isProcessing}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Select a field agent to assign to all delivery challans. Leave empty to remove agent assignment.
                </p>
              </div>

              {/* Invoice List */}
              <div className="border rounded-lg max-h-60 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Delivery Status</TableHead>
                      <TableHead>Current Agent</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {validInvoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-medium">{invoice.voucher_number}</TableCell>
                        <TableCell>{invoice.party?.name || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {invoice.delivery_status || 'Not Delivered'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {invoice.related_delivery_challans?.[0]?.assigned_to_party_name || 'Not assigned'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Warning about invoices without delivery challans */}
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Only invoices with existing delivery challans will be updated. Invoices without delivery challans will be skipped.
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={isProcessing}>
                Cancel
              </Button>
              <Button onClick={onConfirm} disabled={isProcessing || validInvoices.length === 0}>
                <UserCheck className="h-4 w-4 mr-2" />
                Assign to {validInvoices.length} Invoice{validInvoices.length !== 1 ? 's' : ''}
              </Button>
            </DialogFooter>
          </>
        )}

        {isProcessing && progress && (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Processing invoices...</span>
                <span className="font-medium">
                  {progress.current} / {progress.total}
                </span>
              </div>
              <Progress value={progressPercentage} className="h-2" />
              {progress.currentInvoice && (
                <p className="text-sm text-muted-foreground">
                  Updating invoice: {progress.currentInvoice}
                </p>
              )}
              {progress.skipped !== undefined && progress.skipped > 0 && (
                <p className="text-sm text-warning">
                  Skipped {progress.skipped} invoice{progress.skipped > 1 ? 's' : ''} without delivery challans
                </p>
              )}
            </div>

            {progress.errors && progress.errors.length > 0 && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-1">
                    <p className="font-medium">Errors occurred:</p>
                    {progress.errors.map((error, index) => (
                      <p key={index} className="text-sm">{error}</p>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {progress.current === progress.total && (
              <div className="flex items-center justify-center gap-2 text-success">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Update completed!</span>
              </div>
            )}

            {progress.current === progress.total && (
              <DialogFooter>
                <Button onClick={onClose}>Close</Button>
              </DialogFooter>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
